import React, { useState, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { COLORS, L2_PAGE_SIZE } from '../../pages/app/reports/groups-teams-activity/constants';
import { TeamRow, SortDirection } from '../../pages/app/reports/groups-teams-activity/types';
import { StatusDot } from './status-dot';
import { ProgressBar } from './progress-bar';
import { TrendIndicator } from './trend-indicator';
import { TagPills } from './tag-pills';
import { SearchInput } from './search-input';

interface TeamsTableProps {
    teams: TeamRow[];
    defaultTeam?: TeamRow;
    onTeamClick: (team: TeamRow) => void;
}

type SortColumn = 'name' | 'users' | 'active' | 'inactive' | 'pct' | 'trend';

const columnLabels: Record<SortColumn, string> = {
    name: 'team name',
    users: 'users',
    active: 'active',
    inactive: 'inactive',
    pct: '% active',
    trend: 'trend',
};

const thStyle = {
    fontSize: '10px',
    color: COLORS.grayMedium,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '.5px',
    padding: '8px 12px',
    textAlign: 'left' as const,
    borderBottom: `2px solid ${COLORS.border}`,
    whiteSpace: 'nowrap' as const,
    cursor: 'pointer',
    userSelect: 'none' as const,
    transition: 'color .15s',
};

const tdStyle = {
    padding: '10px 12px',
    borderBottom: `1px solid ${COLORS.borderLight}`,
    verticalAlign: 'middle' as const,
    fontSize: '13px',
};

export const TeamsTable: React.FC<TeamsTableProps> = ({ teams, defaultTeam, onTeamClick }) => {
    const [sortCol, setSortCol] = useState<SortColumn>('pct');
    const [sortDir, setSortDir] = useState<SortDirection>('desc');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);

    const handleSort = (col: SortColumn) => {
        if (sortCol === col) {
            setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
        } else {
            setSortCol(col);
            setSortDir('desc');
        }
        setPage(0);
    };

    const filtered = useMemo(() => {
        if (!search) return teams;
        const q = search.toLowerCase();
        return teams.filter(
            (t) =>
                t.name.toLowerCase().includes(q) ||
                t.tags.some((tag) => tag.toLowerCase().includes(q))
        );
    }, [teams, search]);

    const sorted = useMemo(() => {
        return [...filtered].sort((a, b) => {
            let va: any, vb: any;
            switch (sortCol) {
                case 'name':
                    va = a.name.toLowerCase();
                    vb = b.name.toLowerCase();
                    return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
                case 'users': va = a.userCount; vb = b.userCount; break;
                case 'active': va = a.activeCount; vb = b.activeCount; break;
                case 'inactive': va = a.inactiveCount; vb = b.inactiveCount; break;
                case 'pct': va = a.activePercent; vb = b.activePercent; break;
                case 'trend': va = a.trendValue; vb = b.trendValue; break;
                default: va = a.activePercent; vb = b.activePercent;
            }
            return sortDir === 'asc' ? va - vb : vb - va;
        });
    }, [filtered, sortCol, sortDir]);

    const totalPages = Math.ceil(sorted.length / L2_PAGE_SIZE);
    const paged = sorted.slice(page * L2_PAGE_SIZE, (page + 1) * L2_PAGE_SIZE);
    const isLastPage = (page + 1) * L2_PAGE_SIZE >= sorted.length;
    const showingStart = page * L2_PAGE_SIZE + 1;
    const showingEnd = Math.min((page + 1) * L2_PAGE_SIZE, sorted.length);

    const SortArrows = ({ col }: { col: SortColumn }) => (
        <span style={{ display: 'inline-flex', flexDirection: 'column', marginLeft: '3px', verticalAlign: 'middle', lineHeight: 0, position: 'relative', top: '-1px' }}>
            <span style={{ fontSize: '7px', lineHeight: '7px', color: sortCol === col && sortDir === 'asc' ? COLORS.primary : '#C4C9D2' }}>{'▲'}</span>
            <span style={{ fontSize: '7px', lineHeight: '7px', color: sortCol === col && sortDir === 'desc' ? COLORS.primary : '#C4C9D2' }}>{'▼'}</span>
        </span>
    );

    return (
        <Box
            sx={{
                backgroundColor: COLORS.cardBg,
                borderRadius: '10px',
                border: `1px solid ${COLORS.border}`,
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '14px 18px',
                    borderBottom: `1px solid ${COLORS.borderLight}`,
                }}
            >
                <Box>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>Teams</Typography>
                    <Typography sx={{ fontSize: '11px', color: COLORS.grayMedium }}>
                        Sorted by {columnLabels[sortCol]} {sortDir === 'desc' ? '↓' : '↑'}
                    </Typography>
                </Box>
                <SearchInput
                    value={search}
                    onChange={(v) => { setSearch(v); setPage(0); }}
                    placeholder="Search teams…"
                    resultCount={search ? filtered.length : undefined}
                />
            </Box>

            {/* Table */}
            <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th style={{ ...thStyle, width: '200px' }} onClick={() => handleSort('name')}>
                                Team <SortArrows col="name" />
                            </th>
                            <th style={{ ...thStyle, textAlign: 'right' }} onClick={() => handleSort('users')}>
                                Users <SortArrows col="users" />
                            </th>
                            <th style={{ ...thStyle, textAlign: 'right' }} onClick={() => handleSort('active')}>
                                Active <SortArrows col="active" />
                            </th>
                            <th style={{ ...thStyle, textAlign: 'right' }} onClick={() => handleSort('inactive')}>
                                Inactive <SortArrows col="inactive" />
                            </th>
                            <th style={{ ...thStyle, width: '160px' }} onClick={() => handleSort('pct')}>
                                % Active <SortArrows col="pct" />
                            </th>
                            <th style={{ ...thStyle, textAlign: 'center' }} onClick={() => handleSort('trend')}>
                                Trend <SortArrows col="trend" />
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {paged.map((team) => (
                            <TeamRowComponent key={team.id} team={team} onClick={() => onTeamClick(team)} />
                        ))}
                        {isLastPage && !search && defaultTeam && (
                            <TeamRowComponent team={defaultTeam} onClick={() => onTeamClick(defaultTeam)} />
                        )}
                    </tbody>
                </table>
            </Box>

            {/* Pagination */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 18px',
                    fontSize: '11px',
                    color: COLORS.grayDark,
                    borderTop: `1px solid ${COLORS.border}`,
                }}
            >
                <Typography sx={{ fontSize: '11px', color: COLORS.grayDark }}>
                    Showing {showingStart}–{showingEnd} of {sorted.length} teams
                </Typography>
                <Box sx={{ display: 'flex', gap: '8px' }}>
                    <Typography
                        onClick={() => page > 0 && setPage(page - 1)}
                        sx={{
                            fontSize: '11px',
                            color: page > 0 ? COLORS.primary : COLORS.grayMedium,
                            cursor: page > 0 ? 'pointer' : 'default',
                            fontWeight: 500,
                            '&:hover': page > 0 ? { textDecoration: 'underline' } : {},
                        }}
                    >
                        &#8592; Prev
                    </Typography>
                    <Typography sx={{ color: COLORS.grayMedium }}>|</Typography>
                    <Typography
                        onClick={() => !isLastPage && setPage(page + 1)}
                        sx={{
                            fontSize: '11px',
                            color: !isLastPage ? COLORS.primary : COLORS.grayMedium,
                            cursor: !isLastPage ? 'pointer' : 'default',
                            fontWeight: 500,
                            '&:hover': !isLastPage ? { textDecoration: 'underline' } : {},
                        }}
                    >
                        Next &#8594;
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

// Individual team row component
const TeamRowComponent: React.FC<{ team: TeamRow; onClick: () => void }> = ({ team, onClick }) => {
    const isDefault = team.isDefault;

    return (
        <tr
            onClick={onClick}
            style={{
                cursor: 'pointer',
                borderTop: isDefault ? `2px solid ${COLORS.border}` : undefined,
            }}
            onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = '#F8FAFC';
            }}
            onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = '';
            }}
        >
            <td style={tdStyle}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <StatusDot percent={team.activePercent} isDefault={isDefault} />
                    <Box>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontSize: '13px',
                                color: isDefault ? COLORS.grayMedium : COLORS.textPrimary,
                                fontStyle: isDefault ? 'italic' : 'normal',
                            }}
                        >
                            {team.name}
                        </Typography>
                        <TagPills tags={team.tags} />
                    </Box>
                </Box>
            </td>
            <td style={{ ...tdStyle, textAlign: 'right', color: isDefault ? COLORS.grayMedium : undefined }}>
                {team.userCount}
            </td>
            <td
                style={{
                    ...tdStyle,
                    textAlign: 'right',
                    color: isDefault ? COLORS.grayMedium : COLORS.primary,
                    fontWeight: isDefault ? 400 : 600,
                }}
            >
                {team.activeCount}
            </td>
            <td
                style={{
                    ...tdStyle,
                    textAlign: 'right',
                    color: isDefault ? COLORS.grayMedium : COLORS.red,
                }}
            >
                {team.inactiveCount}
            </td>
            <td style={tdStyle}>
                <ProgressBar percent={team.activePercent} isDefault={isDefault} />
            </td>
            <td style={{ ...tdStyle, textAlign: 'center' }}>
                <TrendIndicator direction={team.trend} value={team.trendValue} />
            </td>
        </tr>
    );
};
